import type { RouteLocationRaw } from 'vue-router';
import { wireText } from '@/helpers/wireText';

/** Kun eksakte API-/canonical-verdier — ingen aliaser (f.eks. timesheet→time-sheet). */
export type MenuTargetType =
  | 'folder'
  | 'table'
  | 'data-view'
  | 'report'
  | 'query'
  | 'external-link'
  | 'tripletex'
  | 'tripletex-product'
  | 'tripletex-invoice'
  | 'tripletex-ledger'
  | 'tripletex-employee'
  | 'devops-workitem'
  | 'snomedct-concepts'
  | 'kodeverk-nkpk'
  | 'time-sheet';

export interface MenuTargetContext {
  projectSlug?: string | null;
}

export interface TargetRouteSpec {
  name: string;
  params?: Record<string, unknown>;
}

export function toRouteLocationRaw (
  spec: TargetRouteSpec | null,
  menuItemKey?: string | null
): RouteLocationRaw | false {
  if (!spec) return false;
  const route: Record<string, unknown> = {
    name: spec.name,
    params: spec.params || {}
  };
  const normalizedMenuItemKey = String(menuItemKey || '').trim();
  if (normalizedMenuItemKey) {
    route.state = { menuItemKey: normalizedMenuItemKey };
  }
  return route as RouteLocationRaw;
}

export function buildTargetMeta (
  target: {
    targetType?: string | null;
    /** Når satt (fra API: f.eks. targetDisplayName / søk), brukes som type-etikett. */
    presentationLabel?: string | null;
    schemaName?: string | null;
    tableName?: string | null;
    workspaceName?: string | null;
    modelName?: string | null;
    description?: string | null;
    targetPublicId?: string | null;
  },
  includeType = true
): string {
  const parts: string[] = [];
  if (includeType) {
    const typeLabel = String(target?.presentationLabel || '').trim();
    if (typeLabel) parts.push(typeLabel);
  }
  if (target?.schemaName && target?.tableName) {
    parts.push(`${target.schemaName}.${target.tableName}`);
  }
  if (target?.workspaceName) parts.push(target.workspaceName);
  if (target?.modelName) parts.push(target.modelName);
  if (target?.description) parts.push(target.description);
  if (parts.length > 0) return parts.join(' | ');
  return String(target?.targetPublicId || '').trim();
}

export function getMenuContextSupportedRouteNames (): string[] {
  return [
    'table-public',
    'data-view-public',
    'report-public',
    'TripletexModule',
    'KodeverkModule',
    'DevOpsModule',
    'SnomedCtModule',
    'TimeSheetModule',
    'RiskManagementModule',
    'ProjectPlanningModule'
  ];
}

export function resolveMenuContextPublicId (routeName: string, params: Record<string, unknown>): string {
  const normalizedRouteName = String(routeName || '').trim();
  if (normalizedRouteName === 'TimeSheetModule') return '__timesheet__';
  if (normalizedRouteName === 'RiskManagementModule') return '__risk-management__';
  if (normalizedRouteName === 'ProjectPlanningModule') return '__project-planning__';
  return wireText(params.publicId || '').trim();
}

export function resolveTargetTypeCandidatesFromRoute (
  routeName: string,
  rawPublicId: string
): Array<{
  targetType: MenuTargetType;
  targetPublicId?: string;
  targetSubType?: string;
  fallbackToFirstTypeMatch?: boolean;
}> {
  const normalizedRouteName = String(routeName || '').trim();
  const normalizedPublicId = String(rawPublicId || '').trim();
  const normalizedLower = normalizedPublicId.toLowerCase();

  if (normalizedRouteName === 'SnomedCtModule') {
    return [{ targetType: 'snomedct-concepts', targetPublicId: normalizedPublicId, fallbackToFirstTypeMatch: true }];
  }
  if (normalizedRouteName === 'KodeverkModule') {
    return [{ targetType: 'kodeverk-nkpk', targetPublicId: normalizedPublicId }];
  }
  if (normalizedRouteName === 'DevOpsModule') {
    return [{ targetType: 'devops-workitem', targetPublicId: normalizedPublicId }];
  }
  if (normalizedRouteName === 'TimeSheetModule') {
    return [{ targetType: 'time-sheet', targetPublicId: '/time-sheet' }];
  }
  if (normalizedRouteName === 'TripletexModule') {
    if (normalizedLower === 'products' || normalizedLower === 'product') {
      return [{ targetType: 'tripletex', targetSubType: 'products', targetPublicId: normalizedPublicId || 'products' }];
    }
    if (normalizedLower === 'invoice') {
      return [{ targetType: 'tripletex', targetSubType: 'invoice', targetPublicId: normalizedPublicId }];
    }
    if (normalizedLower === 'ledger') {
      return [{ targetType: 'tripletex', targetSubType: 'ledger', targetPublicId: normalizedPublicId }];
    }
    if (normalizedLower === 'employee') {
      return [{ targetType: 'tripletex', targetSubType: 'employee', targetPublicId: normalizedPublicId }];
    }
  }
  return [];
}
